import type {
  WalletAddressesMonitoringQuery,
  WalletsMonitoringQuery,
} from '../contracts/wallet-monitoring';

const sortUnique = <T extends string>(values?: T[]): T[] | undefined => {
  if (!values?.length) return undefined;
  return [...new Set(values)].sort();
};

export const buildWalletMonitoringQuery = (params: WalletsMonitoringQuery): string => {
  const search = new URLSearchParams();
  search.set('page', String(params.page ?? 1));
  search.set('limit', String(params.limit ?? 25));

  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) search.set('search', trimmedSearch);

  const appendArray = (key: string, values?: string[]) => {
    const unique = sortUnique(values);
    if (!unique?.length) return;
    for (const value of unique) {
      search.append(key, value);
    }
  };

  appendArray('groupCodes', params.groupCodes);
  appendArray('walletKinds', params.walletKinds);
  appendArray('balanceSources', params.balanceSources);
  appendArray('networkCodes', params.networkCodes);
  appendArray('currencyCodes', params.currencyCodes);
  appendArray('tokens', params.tokens);
  appendArray('riskLevels', params.riskLevels);
  appendArray('statuses', params.statuses);

  if (params.amlMonitoring !== undefined) {
    search.set('amlMonitoring', String(params.amlMonitoring));
  }
  if (params.dateFrom) search.set('dateFrom', params.dateFrom);
  if (params.dateTo) search.set('dateTo', params.dateTo);
  if (params.sort) search.set('sort', params.sort);
  if (params.order) search.set('order', params.order);

  return search.toString();
};

export const buildWalletAddressesMonitoringQuery = (
  params: WalletAddressesMonitoringQuery,
): string => {
  const search = new URLSearchParams();
  search.set('page', String(params.page ?? 1));
  search.set('limit', String(params.limit ?? 25));

  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) search.set('search', trimmedSearch);

  const appendArray = (key: string, values?: string[]) => {
    const unique = sortUnique(values);
    if (!unique?.length) return;
    for (const value of unique) {
      search.append(key, value);
    }
  };

  appendArray('networkCodes', params.networkCodes);
  appendArray('currencyCodes', params.currencyCodes);
  appendArray('tokens', params.tokens);
  appendArray('riskLevels', params.riskLevels);
  appendArray('statuses', params.statuses);

  if (params.amlMonitoring !== undefined) {
    search.set('amlMonitoring', String(params.amlMonitoring));
  }
  if (params.dateFrom) search.set('dateFrom', params.dateFrom);
  if (params.dateTo) search.set('dateTo', params.dateTo);

  return search.toString();
};

/** Stable plain object for SWR keys — arrays sorted and deduped. */
export const stableWalletMonitoringParams = (
  params: WalletsMonitoringQuery,
): Record<string, unknown> => {
  const stable: Record<string, unknown> = {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
  };

  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) stable.search = trimmedSearch;

  const groupCodes = sortUnique(params.groupCodes);
  if (groupCodes) stable.groupCodes = groupCodes;

  const walletKinds = sortUnique(params.walletKinds);
  if (walletKinds) stable.walletKinds = walletKinds;

  const balanceSources = sortUnique(params.balanceSources);
  if (balanceSources) stable.balanceSources = balanceSources;

  const networkCodes = sortUnique(params.networkCodes);
  if (networkCodes) stable.networkCodes = networkCodes;

  const currencyCodes = sortUnique(params.currencyCodes);
  if (currencyCodes) stable.currencyCodes = currencyCodes;

  const tokens = sortUnique(params.tokens);
  if (tokens) stable.tokens = tokens;

  const riskLevels = sortUnique(params.riskLevels);
  if (riskLevels) stable.riskLevels = riskLevels;

  const statuses = sortUnique(params.statuses);
  if (statuses) stable.statuses = statuses;

  if (params.amlMonitoring !== undefined) stable.amlMonitoring = params.amlMonitoring;
  if (params.dateFrom) stable.dateFrom = params.dateFrom;
  if (params.dateTo) stable.dateTo = params.dateTo;
  if (params.sort) stable.sort = params.sort;
  if (params.order) stable.order = params.order;

  return stable;
};

export const stableWalletAddressesParams = (
  params: WalletAddressesMonitoringQuery,
): Record<string, unknown> => {
  const stable: Record<string, unknown> = {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
  };

  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) stable.search = trimmedSearch;

  const networkCodes = sortUnique(params.networkCodes);
  if (networkCodes) stable.networkCodes = networkCodes;

  const currencyCodes = sortUnique(params.currencyCodes);
  if (currencyCodes) stable.currencyCodes = currencyCodes;

  const tokens = sortUnique(params.tokens);
  if (tokens) stable.tokens = tokens;

  const riskLevels = sortUnique(params.riskLevels);
  if (riskLevels) stable.riskLevels = riskLevels;

  const statuses = sortUnique(params.statuses);
  if (statuses) stable.statuses = statuses;

  if (params.amlMonitoring !== undefined) stable.amlMonitoring = params.amlMonitoring;
  if (params.dateFrom) stable.dateFrom = params.dateFrom;
  if (params.dateTo) stable.dateTo = params.dateTo;

  return stable;
};
